import axios from 'axios';
import { supabase, getCachedAccessToken, refreshAccessToken } from '@/shared/lib/supabase';
import { markMfaRequired, markStaffEnrolmentRequired, waitForMfaSatisfied } from '@/shared/hooks/useAuthMfa';
import { useWorkspaceStore, getPersistedWorkspaceId } from '@/shared/stores/workspace.store';
import { rememberReturnPath } from '@/shared/lib/return-path';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * The caller shows plan refusals itself (PLAN_REQUIRED, LIMIT_REACHED):
     * no global upgrade toast on top.
     */
    handlesPlanErrors?: boolean;
    /** Set on the replay after a 401 + token refresh, so it is replayed once. */
    _authRetried?: boolean;
    /** Set on the replay after the code step (401 MFA_REQUIRED). */
    _mfaRetried?: boolean;
  }
}

const api = axios.create({
  // Every API route is versioned under /api/v1 (setGlobalPrefix in the API).
  baseURL: import.meta.env.VITE_API_URL ?? '/api/v1',
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach auth token and workspace ID to every request. We read the cached
// token synchronously instead of awaiting supabase.auth.getSession() — the
// latter can block on the Supabase auth lock during token refresh and add
// seconds of latency to every API call.
api.interceptors.request.use(async (config) => {
  let token = getCachedAccessToken();

  // First request after a hard reload may fire before the SDK has hydrated
  // its in-memory session — fall back to getSession() once. Subsequent
  // requests hit the cached token instantly.
  if (!token) {
    const { data } = await supabase.auth.getSession();
    token = data.session?.access_token ?? null;
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Attach current workspace ID for WorkspaceGuard
  // Fall back to the persisted id so requests fired before /workspaces/me
  // hydrates the store (e.g. right after a fresh page load) still carry
  // X-Workspace-Id and don't 403 in WorkspaceGuard.
  //
  // A request that names its brand itself (deleting a brand, confirming a
  // payment for the account being billed) keeps that header: the brand in
  // view is only the default.
  const workspaceId =
    useWorkspaceStore.getState().currentWorkspace?.id ?? getPersistedWorkspaceId();
  const explicit =
    typeof config.headers.has === 'function'
      ? config.headers.has('X-Workspace-Id')
      : Boolean((config.headers as Record<string, unknown>)['X-Workspace-Id']);
  if (workspaceId && !explicit) {
    config.headers['X-Workspace-Id'] = workspaceId;
  }

  return config;
});

/** Broadcast when a request fails because the plan does not allow it. */
export const PLAN_REQUIRED_EVENT = 'unipostudio:plan-required';

/** A plan a refusal can name: one that is sold (ADR-007). */
export type RequiredPlan = 'pro' | 'agency';

export interface PlanRequiredDetail {
  code: string;
  /** The cheapest plan that allows it, when the API named one. */
  requiredPlan: RequiredPlan | null;
}


/**
 * The plan a 403 names, from `error.details.requiredPlan` (where the API's
 * exception filter puts it) or a flat `requiredPlan`. Null for anything
 * else, so an unknown value never reaches a translation key.
 */
function requiredPlanOf(body: unknown): RequiredPlan | null {
  if (!body || typeof body !== 'object') return null;
  const error = (body as { error?: unknown }).error;
  const payload = (error && typeof error === 'object' ? error : body) as {
    requiredPlan?: unknown;
    details?: { requiredPlan?: unknown } | null;
  };
  const raw = payload.details?.requiredPlan ?? payload.requiredPlan;
  if (typeof raw !== 'string') return null;
  const id = raw.toLowerCase();
  if (id === 'agency') return 'agency';
  // Classic is not sold: an older API naming it is offered Pro, which passes
  // every gate Classic does.
  return id === 'pro' || id === 'classic' ? 'pro' : null;
}

/** `error.code` of an API error body (`{ success: false, error: { code } }`), or a flat `code`. */
function errorCodeOf(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const error = (body as { error?: unknown }).error;
  const code = error && typeof error === 'object' ? (error as { code?: unknown }).code : (body as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

/**
 * 401 MFA_REQUIRED (apps/api common/auth/mfa.ts): the person has a verified
 * authenticator factor and the token is aal1. Not an expired session: the
 * guards send them to the code step, and signing them out would only make
 * them type the password again before the same step.
 */
export const MFA_REQUIRED_CODE = 'MFA_REQUIRED';
/** 409: the post changed since it was read (apps/api posts.service POST_CHANGED). */
export const POST_CHANGED_CODE = 'POST_CHANGED';
/** 403: a staff account without a verified factor called a console route. */
export const MFA_ENROLMENT_REQUIRED_CODE = 'MFA_ENROLMENT_REQUIRED';
/** 503: the API could not check the factor list; nothing was decided. Retry, never sign out. */
export const MFA_CHECK_UNAVAILABLE_CODE = 'MFA_CHECK_UNAVAILABLE';

// Prevent multiple 401 handlers from firing simultaneously
let isHandling401 = false;

// Dedupe window for global PLAN_REQUIRED toasts (a page can fire several
// gated requests at once — surface one upgrade prompt, not a stack).
let lastPlanRequiredAt = 0;

// Don't bounce to /auth for the first ~3 seconds after a fresh sign-in. Some
// races (e.g., a stray request firing with a stale token while the new session
// is still propagating) can trigger an immediate 401 that would otherwise
// boot the user right back to /auth — the loop the user reported.
let lastSignInAt = 0;
export function markSignInTime(): void {
  lastSignInAt = Date.now();
}

// Unwrap standard API envelope { success, data } so services receive the inner payload
api.interceptors.response.use((response) => {
  const body = response.data;
  if (body && typeof body === 'object' && 'success' in body) {
    // Only unwrap successful responses — pass errors through with { success, error }
    if (body.success && 'data' in body) {
      response.data = body.data;
    }
  }
  return response;
});

// Global error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;

      if (status === 401 && errorCodeOf(error.response?.data) === MFA_REQUIRED_CODE) {
        const { data } = await supabase.auth.getSession().catch(() => ({ data: { session: null } }));
        // The guards now show the code step (MfaChallenge runs
        // mfa.challenge/verify). The request waits for it and is replayed
        // with the aal2 token; a sign-out instead rejects it.
        markMfaRequired(data.session);
        const original = error.config;
        if (!original || original._mfaRetried) return Promise.reject(error);
        const token = await waitForMfaSatisfied();
        if (!token) return Promise.reject(error);
        original._mfaRetried = true;
        original.headers.Authorization = `Bearer ${token}`;
        return api.request(original);
      }

      if (status === 403 && errorCodeOf(error.response?.data) === MFA_ENROLMENT_REQUIRED_CODE) {
        markStaffEnrolmentRequired();
        return Promise.reject(error);
      }

      if (status === 401) {
        // An expired token (a laptop that slept past the hour, a refresh that
        // lost a race): refresh once and replay before calling it a sign-out.
        const original = error.config;
        if (original && !original._authRetried) {
          original._authRetried = true;
          const fresh = await refreshAccessToken();
          if (fresh) {
            original.headers.Authorization = `Bearer ${fresh}`;
            return api.request(original);
          }
        }
      }

      if (status === 401 && !isHandling401) {
        const url = error.config?.url ?? '<unknown>';
        const inGracePeriod = Date.now() - lastSignInAt < 3000;
        console.warn(
          `[UniPostudio] 401 from ${url}${inGracePeriod ? ' (within sign-in grace period — not signing out)' : ''}`,
        );
        if (inGracePeriod) {
          // Don't bounce on a stale-request race right after sign-in. React
          // Query will retry the call once the new token has propagated.
          return Promise.reject(error);
        }
        isHandling401 = true;
        try {
          // Session expired — sign out fully before redirecting
          await supabase.auth.signOut();
        } catch {
          // The redirect below still happens; the reload drops the session.
        } finally {
          // The reload wipes in-memory state, but the persisted workspace id
          // survives it — drop it so the next account to sign in on this
          // browser doesn't send the previous user's X-Workspace-Id.
          useWorkspaceStore.getState().reset();
          // Keep the page the person was on: signing in again lands back on it
          // (AuthGuard reads it), not on the home screen. The reload drops
          // router state, so sessionStorage carries it.
          rememberReturnPath(`${window.location.pathname}${window.location.search}${window.location.hash}`);
          isHandling401 = false;
          window.location.href = '/auth';
        }
        return new Promise(() => {}); // Prevent further processing
      }

      if (status === 409 && errorCodeOf(error.response?.data) === POST_CHANGED_CODE) {
        // Someone else changed the post first (another tab, a teammate, an
        // approval link). The caller's toast says so in the reader's words
        // (errors.postChanged); every post view is refetched so the next try
        // starts from what is really there.
        // Loaded on demand: api.ts is imported by modules (and tests) that
        // never touch the query cache.
        void import('@/shared/lib/query-client').then(({ queryClient }) => queryClient.invalidateQueries({ queryKey: ['posts'] }));
      }

      if (status === 403) {
        // Plan-gate errors carry a structured PLAN_REQUIRED code. Broadcast a
        // global event so the shell can surface an upgrade toast even when the
        // calling component has no error handling of its own — plan errors
        // must NEVER fail silently. Deduped so a burst of gated requests
        // (e.g. a page firing 3 queries) shows one toast, not three.
        const body = error.response?.data;
        const code = body?.error?.code ?? body?.code;
        // Plan-gate AND quota-exhausted AND seat-limit errors are all "you need
        // to upgrade / you've hit a cap" — surface one global toast so none of
        // them fail silently when the calling component has no handler.
        if (error.config?.handlesPlanErrors) {
          // The caller says what the plan allows in its own words.
        } else if (
          code === 'PLAN_REQUIRED' ||
          code === 'LIMIT_REACHED' ||
          code === 'TEAM_SEAT_LIMIT_REACHED'
        ) {
          // Only the plan it needs travels on: the listener writes the line in
          // the reader's language. The server's message is English and is
          // never shown.
          const detail: PlanRequiredDetail = { code, requiredPlan: requiredPlanOf(body) };
          if (Date.now() - lastPlanRequiredAt > 3000) {
            lastPlanRequiredAt = Date.now();
            window.dispatchEvent(new CustomEvent<PlanRequiredDetail>(PLAN_REQUIRED_EVENT, { detail }));
          }
        } else {
          console.error('[UniPostudio] Forbidden: insufficient permissions.');
        }
      }

      if (status && status >= 500) {
        console.error('[UniPostudio] Server error:', error.response?.data);
      }

      // Retry once on transient upstream errors (502/503/504/network).
      // These typically only happen during the few-second window when
      // pm2 is restarting the API after a deploy. A single retry after
      // 1.5s is invisible to the user and gets through.
      const isTransient =
        status === 502 || status === 503 || status === 504 ||
        // Browser-level network failures (nginx down, DNS hiccup, etc.)
        (!error.response && error.code === 'ERR_NETWORK');
      const config = error.config as (typeof error.config & { _retried?: boolean }) | undefined;
      // Only requests that are safe to send twice. A POST, PUT, PATCH or
      // DELETE may already have done its work before the gateway gave up
      // (a brand created, an extra brand billed): never repeat it.
      const method = (config?.method ?? 'get').toUpperCase();
      const idempotent = method === 'GET' || method === 'HEAD';
      if (isTransient && idempotent && config && !config._retried) {
        config._retried = true;
        await new Promise((r) => setTimeout(r, 1500));
        return api.request(config);
      }
    }

    return Promise.reject(error);
  },
);

export { api };
